// Single source of truth for the "virtual" sub-dashboard projects (Finance,
// O&M, HR) that are injected client-side alongside a department's real,
// DB-seeded projects. These id/name pairs used to be hand-duplicated across
// Navbar.js, ExcoLandingPage.js, UsersPage.js and DepartmentDashboardsPage.js
// — a display-name edit in only one copy caused a real dashboard to silently
// stop resolving (the "BG Data List Final" vs "BG Data Final List" bug).
// Every consumer must import from here instead of keeping a local copy.
import { normalizeAlphaNumLower } from './string';

export const FINANCE_DASHBOARD_PROJECTS = [
  { id: 'finance-actional-bg-details', name: 'Actional BG Details', isVirtual: true },
  { id: 'finance-bg-data-final-list', name: 'BG Data List Final', isVirtual: true },
  { id: 'finance-cash-emd-list', name: 'Cash EMD List', isVirtual: true },
  { id: 'finance-claim-mis-list', name: 'Claim MIS', isVirtual: true },
  { id: 'finance-insurance-mis-list', name: 'Insurance MIS', isVirtual: true }
];

export const OM_DASHBOARD_PROJECTS = [
  { id: 'om-nfs-frt-technician-dashboard', name: 'NFS FRT & Technician', isVirtual: true },
  { id: 'om-attendance-dashboard', name: 'FRT & Technician Attendance', isVirtual: true }
];

export const HR_DASHBOARD_PROJECTS = [
  { id: 'hr-main-dashboard', name: 'HR Main Dashboard', isVirtual: true },
  { id: 'hr-leadership-tm', name: 'Leadership & TM', isVirtual: true },
  { id: 'hr-learning-development', name: 'Learning & Development', isVirtual: true },
  { id: 'hr-digital-enablement', name: 'Digital & Enablement', isVirtual: true }
];

// Extra normalized-name variants seen in real, DB-seeded project rows that
// represent the SAME dashboard as one of the virtual projects above (legacy
// names, alternate spellings). Used to filter real duplicates out of the
// merged (virtual + real) list shown to the user. Kept as a superset across
// all three consumers (Navbar, ExcoLandingPage, UsersPage) — previously these
// three files carried slightly different variants of the HR set, which meant
// a real HR project row could show as a visible duplicate in one screen but
// not another.
const FINANCE_EXTRA_DUPLICATE_KEYS = ['bg', 'cashemds', 'bgdatafinallist', 'claimmislist', 'insurancemislist'];
const OM_EXTRA_DUPLICATE_KEYS = ['omattendancedashboard', 'frttechnicianattendance', 'frttechnicianattendancedashboard', 'nfsfrttechnician'];
const HR_EXTRA_DUPLICATE_KEYS = ['leadershipandtm', 'hrmaindashboard', 'learningdevelopment', 'digitalenablement'];

const buildDuplicateNameSet = (projects, extraKeys) => {
  const set = new Set(extraKeys);
  projects.forEach((project) => set.add(normalizeAlphaNumLower(project.name)));
  return set;
};

export const FINANCE_DUPLICATE_NAME_SET = buildDuplicateNameSet(FINANCE_DASHBOARD_PROJECTS, FINANCE_EXTRA_DUPLICATE_KEYS);
export const OM_DUPLICATE_NAME_SET = buildDuplicateNameSet(OM_DASHBOARD_PROJECTS, OM_EXTRA_DUPLICATE_KEYS);
export const HR_DUPLICATE_NAME_SET = buildDuplicateNameSet(HR_DASHBOARD_PROJECTS, HR_EXTRA_DUPLICATE_KEYS);

// Normalized project display-name -> canonical virtual id, derived directly
// from the lists above so it can never drift out of sync with what's shown
// in the dropdown (the exact class of bug this module exists to prevent).
export const FINANCE_PROJECT_NAME_TO_ID = FINANCE_DASHBOARD_PROJECTS.reduce((acc, project) => {
  acc[normalizeAlphaNumLower(project.name)] = project.id;
  return acc;
}, {});
